import { set } from 'lodash';
import filesService from "@/pages/FilePage/services/files.service";
import { AnalyticService } from "@/services/AnalyticService";
import AuthService from "@/services/AuthService";
import { UploadFileService } from "@/services/UploadFileService";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import { ajax } from "rxjs/ajax";
import { filter, map, switchMap } from "rxjs/operators";
import type {
  BootstrapState,
  RootEpic,
  SystemConfig,
} from "../../common/type-state";
import { FolderService } from "../../services/FolderService";

import { BimApi } from "@/api/bim.api";
import { ConversionApi } from "@/api/conversion.api";
import { StreamApi } from "@/api/stream.api";
import { WorkflowApi } from "@/api/workflow.api";

import projectsService from "../../services/projectsApi";
import drawingsService from "@/pages/DrawingsPage/services/DrawingsService";
import ExtractApi from "@/pages/FilePage/services/extractionFile.service";
import { DOCAIApi } from "@/services/DocAIService";
import deleteFileService from "../deleteFile/deleteFileService";
import archiveFileService from "../archiveFile/archiveFileService";
import { DriveService } from "@/services/DriveService";
import { StorageService } from "@/pages/SettingPage/StorageManagement/services/storageApi";
import { WorkflowAnalyticService } from '@/services/WorkflowAnalyticService';

console.log("-=======================", window.location.origin);
const PATH_SYSTEM_CONFIG = `${
  window.location.origin || ""
}/assets/system-config.json`;
const USE_DEFAULT_CONFIG = import.meta.env.VITE_USE_DEFAULT_CONFIG === "true";

const DEFAULT_CONFIG: SystemConfig = {
  protocol: import.meta.env.VITE_HOST_PROTOCOL || "http",
  hostStream: import.meta.env.VITE_HOST_STREAM || "/",
  hostFile: import.meta.env.VITE_HOST_FILE || "",
  hostConversion: import.meta.env.VITE_HOST_CONVERSION || "/",
  hostBim: import.meta.env.VITE_HOST_BIM || "/",
  hostIdentity: import.meta.env.VITE_HOST_IDENTITY || "/",
  modeRender: (import.meta.env.VITE_MODE_RENDER as "csr" | "ssr") || "csr",
  baseName: import.meta.env.VITE_BASENAME || "/",
  serverDomain: import.meta.env.VITE_SERVER_DOMAIN || "/",
  hostDOCAI: import.meta.env.VITE_HOST_DOCAI || "/",
  File: import.meta.env.VITE_FILE || "/",
  authenUrl: import.meta.env.VITE_AUTHEN_URL || "https://localhost:7059",
  hostApp: import.meta.env.VITE_HOST_APP || "https://api.inertia.hicas.vn",
  hostAnalysis: import.meta.env.VITE_HOST_ANALYSIS || "",
  viewPDF: import.meta.env.VITE_VIEW_PDF || "https://docai.inertia.hicas.vn",
  hostSignalR: import.meta.env.VITE_HOST_SIGNALR || "http://10.0.0.105:8001",
  chatbot: import.meta.env.VITE_CHATBOT || "",
  hostProject: import.meta.env.VITE_HOST_PROJECT || "",
  hostWorkflow: import.meta.env.VITE_HOST_WORKFLOW || "https://dev-wm.hicas.vn",
  tenant: import.meta.env.VITE_TENANT || "hicas", // Default tenant
  oAuth: {
    clientId: import.meta.env.VITE_OAUTH_CLIENT_ID || "CXM",
    clientSecret: import.meta.env.VITE_OAUTH_CLIENT_SECRET || "ConstruxivViewersecret",
    scope: import.meta.env.VITE_OAUTH_SCOPE || "offline_access API",
  },
};

const initialStateBootstrap: BootstrapState = {
  systemConfig: DEFAULT_CONFIG,
  isSuccess: false,
};

const updateHostService = (config: SystemConfig) => {
  FolderService.setConfig(config);
  AnalyticService.setConfig(config);
  AuthService.setConfig(config);
  StreamApi.setConfig(config);
  ConversionApi.setConfig(config);
  BimApi.setConfig(config);
  UploadFileService.setConfig(config);
  filesService.setConfig(config);
  projectsService.setConfig(config);
  drawingsService.setConfig(config);
  ExtractApi.setConfig(config);
  DOCAIApi.setConfig(config);
  deleteFileService.setConfig(config);
  archiveFileService.setConfig(config);
  projectsService.setConfig(config);
  DriveService.setConfig(config);
  WorkflowApi.setConfig(config);
  StorageService.setConfig(config);
  WorkflowAnalyticService.setConfig(config);
};

const bootstrap$: RootEpic = (action$) =>
  action$.pipe(
    filter(fetchConfig.match),
    switchMap(() => {
      console.log(
        "[bootstrap$] Fetching system-config.json from:",
        PATH_SYSTEM_CONFIG
      );
      return ajax
        .getJSON(PATH_SYSTEM_CONFIG, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        } as any)
        .pipe(
          map((res) => {
            console.log("[bootstrap$] system-config.json loaded:", res);
            const config = USE_DEFAULT_CONFIG
              ? DEFAULT_CONFIG
              : { ...DEFAULT_CONFIG, ...(res as SystemConfig) };
            console.log("[bootstrap$] Final config to set:", config);
            return bootstrapSlice.actions.setSystemConfig(config);
          })
        );
    })
  );

const bootstrapSlice = createSlice({
  name: "bootstrap",
  initialState: initialStateBootstrap,
  reducers: {
    setSystemConfig: (state, action: PayloadAction<SystemConfig>) => {
      updateHostService(action.payload);
      state.systemConfig = action.payload;
      state.isSuccess = true;
    },
    fetchConfig: (state) => {
      state.isSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state) => {
      state.isSuccess = false;
    });
  },
});

export const BootstrapEpics = [bootstrap$];
export const { fetchConfig } = bootstrapSlice.actions;
export const bootstrapReducer = bootstrapSlice.reducer;
